import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Col, Input, Row, Table } from 'reactstrap';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import CellphoneIcon from 'mdi-react/CellphoneIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import DisplayCounter from '../../_uiComponents/DisplayCounter';
import Loading from '../../../shared/components/custom/Loading';

const p = partial('SponsorViews');

const RANGES = [3, 6, 12];
const PLACEMENTS = ['AppSponsor', 'matchBanner', 'playerCard', 'spotlightTile'];

const formatMonth = (month) => moment(`${month}-01`).format('MMM YYYY');
const formatNumber = (value) => Number(value || 0).toLocaleString();
const placementName = (type) =>
  PLACEMENTS.includes(type) ? p(`placement_${type}`) : type;
// "spotlightTile-<id>/<companyId>" → "<id>"
const itemName = (entityId) =>
  `${entityId || ''}`.replace(/^[^-]+-/, '').replace(/\/[^/]*$/, '') || '-';

// A company's sponsor views from the app: month by month, by placement and
// its most seen items. Numbers come in nightly; past months are final.
const SponsorViewsCard = ({ companyID }) => {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const to = moment().format('YYYY-MM');
    const from = moment()
      .subtract(months - 1, 'months')
      .format('YYYY-MM');
    setLoading(true);
    setFailed(false);
    Api.companies.sponsorViews
      .getCompany(companyID, { from, to })
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyID, months]);

  const latest = data?.months?.[data.months.length - 1];

  return (
    <div className="p-3">
      <Loading loading={loading} />
      <Row className="mb-3 align-items-end">
        <Col md={8}>
          <h4 className="mb-1">{p('title')}</h4>
          <small className="text-muted">{p('info')}</small>
        </Col>
        <Col md={4}>
          <Input
            type="select"
            bsSize="sm"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
          >
            {RANGES.map((count) => (
              <option key={count} value={count}>
                {p('lastMonths', { count })}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      {failed && <p className="text-danger">{p('loadFailed')}</p>}
      {data && !data.months.length && <p>{p('noData')}</p>}

      {data && data.months.length > 0 && (
        <>
          <div className="my-2">
            <DisplayCounter
              id="sponsor-viewers"
              title={p('viewers')}
              text={p('viewersIn', { month: formatMonth(latest.month) })}
              value={formatNumber(latest.viewers)}
              icon={<AccountMultipleIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="sponsor-sessions"
              title={p('sessions')}
              text={p('inPeriod')}
              value={formatNumber(data.totals.sessions)}
              icon={<CellphoneIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="sponsor-views"
              title={p('views')}
              text={p('inPeriod')}
              value={formatNumber(data.totals.views)}
              icon={<EyeOutlineIcon style={{ width: 18 }} />}
            />
          </div>

          <div style={{ height: 300 }} className="my-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data.months}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e0e0e0"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#666', fontSize: 11 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#666', fontSize: 12 }}
                />
                <Tooltip
                  labelFormatter={formatMonth}
                  formatter={(value) => formatNumber(value)}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="viewers"
                  name={p('viewers')}
                  stroke="#007bff"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  name={p('sessions')}
                  stroke="#000000"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {latest && !latest.frozen && (
            <small className="text-muted d-block mb-3">
              {p('ongoingMonth', { month: formatMonth(latest.month) })}
            </small>
          )}

          <Row>
            <Col lg={5} className="mb-3">
              <h5>{p('byPlacement')}</h5>
              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    <th>{p('placement')}</th>
                    <th>{p('sessions')}</th>
                    <th>{p('views')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.placements.map((row) => (
                    <tr key={row.type}>
                      <td>{placementName(row.type)}</td>
                      <td>{formatNumber(row.sessions)}</td>
                      <td>{formatNumber(row.views)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col lg={7} className="mb-3">
              <h5>{p('topItems')}</h5>
              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    <th>{p('placement')}</th>
                    <th>{p('item')}</th>
                    <th>{p('sessions')}</th>
                    <th>{p('views')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={`${row.type}-${row.entityId}`}>
                      <td>{placementName(row.type)}</td>
                      <td>{itemName(row.entityId)}</td>
                      <td>{formatNumber(row.sessions)}</td>
                      <td>{formatNumber(row.views)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default SponsorViewsCard;
